import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Text } from "@chakra-ui/react";
import { Gift } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import FloatingToast from "../components/common/FloatingToast";
import { useAuth } from "./AuthProvider";
import {
  claimReferralRewardReminder,
  clearReferralRewardReminder,
  MY_REWARDS_PATH,
} from "../utils/referral";

const TOAST_DURATION = 10;
const TOAST_SHOW_DELAY_MS = 500;
const BLUE = "#2563EB";

type RewardsReminderContextValue = {
  hasUnseenReferralReward: boolean;
  acknowledgeReferralReward: () => void;
  toast: {
    visible: boolean;
    timeLeft: number;
    dismiss: () => void;
  };
};

const RewardsReminderContext =
  createContext<RewardsReminderContextValue | null>(null);

function isMyRewardsPath(pathname: string) {
  return pathname === MY_REWARDS_PATH || pathname.startsWith(`${MY_REWARDS_PATH}/`);
}

export function RewardsReminderProvider({ children }: { children: ReactNode }) {
  const { role, loading, user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userId = role === "homeowner" ? user?.id : null;
  const userKey = userId == null ? null : String(userId);

  const [hasUnseenReferralReward, setHasUnseenReferralReward] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastTimeLeft, setToastTimeLeft] = useState(TOAST_DURATION);
  const shownForUserRef = useRef<string | null>(null);
  const toastDelayIdRef = useRef<number | null>(null);

  const clearScheduledToast = useCallback(() => {
    if (toastDelayIdRef.current == null) return;
    window.clearTimeout(toastDelayIdRef.current);
    toastDelayIdRef.current = null;
  }, []);

  const acknowledgeReferralReward = useCallback(() => {
    clearScheduledToast();
    clearReferralRewardReminder(userId);
    setHasUnseenReferralReward(false);
    setToastVisible(false);
  }, [clearScheduledToast, userId]);

  useEffect(() => {
    if (loading || role !== "homeowner" || userId == null) {
      clearScheduledToast();
      setHasUnseenReferralReward(false);
      setToastVisible(false);
      shownForUserRef.current = null;
      return;
    }

    const hasReminder = claimReferralRewardReminder(userId);
    if (!hasReminder) {
      clearScheduledToast();
      setHasUnseenReferralReward(false);
      setToastVisible(false);
      return;
    }

    if (isMyRewardsPath(pathname)) {
      acknowledgeReferralReward();
      return;
    }

    setHasUnseenReferralReward(true);
    if (shownForUserRef.current !== userKey) {
      shownForUserRef.current = userKey;
      setToastTimeLeft(TOAST_DURATION);
      clearScheduledToast();
      toastDelayIdRef.current = window.setTimeout(() => {
        toastDelayIdRef.current = null;
        if (!isMyRewardsPath(window.location.pathname)) {
          setToastVisible(true);
        }
      }, TOAST_SHOW_DELAY_MS);
    }
  }, [
    acknowledgeReferralReward,
    clearScheduledToast,
    loading,
    pathname,
    role,
    userId,
    userKey,
  ]);

  useEffect(() => () => clearScheduledToast(), [clearScheduledToast]);

  useEffect(() => {
    if (!toastVisible) return;
    if (toastTimeLeft <= 0) {
      setToastVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(
      () => setToastTimeLeft((current) => current - 1),
      1000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [toastTimeLeft, toastVisible]);

  const contextValue = useMemo<RewardsReminderContextValue>(
    () => ({
      hasUnseenReferralReward,
      acknowledgeReferralReward,
      toast: {
        visible: toastVisible,
        timeLeft: toastTimeLeft,
        dismiss: acknowledgeReferralReward,
      },
    }),
    [
      acknowledgeReferralReward,
      hasUnseenReferralReward,
      toastTimeLeft,
      toastVisible,
    ],
  );

  return (
    <RewardsReminderContext.Provider value={contextValue}>
      {children}
      <FloatingToast
        visible={toastVisible && hasUnseenReferralReward && !isMyRewardsPath(pathname)}
        toastKey="referral-reward-toast"
        accentColor={BLUE}
        icon={<Gift size={15} color="white" />}
        title="Your referral reward is ready"
        description={<Text>Tap to view it in My Rewards</Text>}
        zIndex={9996}
        onClick={() => {
          acknowledgeReferralReward();
          navigate(MY_REWARDS_PATH);
        }}
        onDismiss={acknowledgeReferralReward}
        progressDurationSeconds={TOAST_DURATION}
        progressKey={toastVisible ? "referral-reward-active" : "referral-reward-idle"}
      />
    </RewardsReminderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRewardsReminder(): RewardsReminderContextValue {
  const ctx = useContext(RewardsReminderContext);
  if (!ctx) {
    throw new Error("useRewardsReminder must be used inside RewardsReminderProvider");
  }
  return ctx;
}
